import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../axiosInterceptor';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  status?: string;
  avatar?: string;
  location?: string;
  timezone?: string;
  professional_bio?: string;
  coach_id?: string;
}

interface AdminUserState {
  currentUser: AdminUser | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminUserState = {
  currentUser: null,
  loading: false,
  error: null,
};

// Sign in admin user
export const signIn = createAsyncThunk(
  'adminUser/signIn',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      await axios.post('/api/account/signin', credentials);
      // After successful sign in, fetch user profile
      const userResponse = await axios.get('/api/me');
      return userResponse.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Invalid credentials');
    }
  }
);

// Sign out admin user
export const signOut = createAsyncThunk(
  'adminUser/signOut',
  async (_, { rejectWithValue }) => {
    try {
      await axios.post('/api/account/signout');
      return null;
    } catch (error: any) {
      // Even if signout fails on backend, clear local state
      return null;
    }
  }
);

// Get current admin user details
export const getAdminUser = createAsyncThunk(
  'adminUser/getMe',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/me');
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch admin user');
    }
  }
);

// Get user by ID (admin can fetch any user)
export const getUserById = createAsyncThunk(
  'adminUser/getById',
  async (userId: string, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/users/${userId}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch user');
    }
  }
);

const adminUserSlice = createSlice({
  name: 'adminUser',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAdminUser: (state) => {
      state.loading = false;
      state.currentUser = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Sign in
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action: PayloadAction<AdminUser>) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Sign out
    builder
      .addCase(signOut.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.currentUser = null;
        state.error = null;
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.currentUser = null;
        state.error = null;
      });

    // Get admin user
    builder
      .addCase(getAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminUser.fulfilled, (state, action: PayloadAction<AdminUser>) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(getAdminUser.rejected, (state, action) => {
        state.loading = false;
        // Don't set error for getAdminUser - it's expected when not logged in
      });

    // Get user by ID
    builder
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action: PayloadAction<AdminUser>) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetAdminUser } = adminUserSlice.actions;
export default adminUserSlice.reducer;
